import React, { useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import CloseIcon from '@atlaskit/icon/core/migration/close--cross';
import Select from '@atlaskit/select';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';

import { DEFAULT_FIELDS } from '../../../common/constants';
import { ConditionFilterMessages } from '../../../common/messages';

import { Button, ConditionWrapper } from './styled';
import { type IConditionProps, TYPE_MAPPING } from './types';

const Condition: React.FC<IConditionProps> = ({
	expectedValue,
	field,
	not,
	operation,
	fields,
	update,
	delete: deleteCondition,
	conditionCount,
	isFieldKeyRequired,
	isDisabled,
	isReadOnly,
	autoFocus,
}) => {
	const { formatMessage } = useIntl();

	const fieldSelectValue = useMemo(() => {
		const matchedField = fields.find((f) => f.value === field);
		return matchedField ?? DEFAULT_FIELDS[0];
	}, [field, fields]);

	const Fields = TYPE_MAPPING[fieldSelectValue.type];

	return (
		<ConditionWrapper data-testid="condition-container">
			<Select
				autoFocus={autoFocus}
				options={fields}
				value={fieldSelectValue}
				isDisabled={isDisabled}
				menuIsOpen={isReadOnly ? false : undefined}
				isSearchable={!isReadOnly}
				onChange={(selectedKey) => {
					if (selectedKey) {
						update({
							field: selectedKey.value,
							expectedValue: '',
							fieldFreeForm: selectedKey.type === 'keyValue',
						});
					}
				}}
				menuPosition="fixed"
				label={formatMessage(ConditionFilterMessages.AlertFieldAriaLabel)}
			/>
			<Fields
				field={field}
				fieldType={fieldSelectValue.type}
				update={update}
				expectedValue={expectedValue}
				not={not}
				operation={operation}
				isFieldKeyRequired={isFieldKeyRequired}
				isDisabled={isDisabled}
				isReadOnly={isReadOnly}
			/>
			{conditionCount > 1 && (
				<Button
					onClick={(e, analyticsEvent) => {
						fireUIAnalytics(analyticsEvent, 'removeFilterCondition');
						deleteCondition();
					}}
					isDisabled={isDisabled || isReadOnly}
					iconAfter={
						<CloseIcon
							label={formatMessage(ConditionFilterMessages.DeleteIconLabel)}
							color="currentColor"
						/>
					}
					appearance="subtle"
					aria-label={formatMessage(ConditionFilterMessages.RemoveConditionButtonAriaLabel)}
				/>
			)}
		</ConditionWrapper>
	);
};

export default Condition;
